import { useEffect } from 'react';
import { Headphones, Mail, Shield } from 'lucide-react';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import OfficialSourcesNotice from '@/components/OfficialSourcesNotice';

export default function AboutPage() {
  useEffect(() => {
    document.title = 'Om oss - Beredskapshubben';
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <main className="relative py-8 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover opacity-40"
          style={{ backgroundImage: 'url(/background.png)', backgroundPosition: 'center 80%' }}
        />
        <div className="absolute inset-0 bg-black/60 pointer-events-none" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto bg-card border border-border rounded-lg overflow-hidden">
            {/* Logo-area med bakgrundsbild – full bredd i kortet */}
            <div className="relative flex justify-center py-8">
              <div
                className="absolute inset-0 bg-cover"
                style={{ backgroundImage: 'url(/background.png)', backgroundPosition: 'center 80%' }}
              />
              <div className="relative z-10 bg-black/80 backdrop-blur-sm rounded-2xl px-8 py-4">
                <img
                  src="/logos/logo-color-textunder.svg"
                  alt="Beredskapshubben logotyp"
                  className="h-48 md:h-64 w-auto"
                />
              </div>
            </div>

            {/* Resten av kortets innehåll */}
            <div className="p-6 md:p-8 space-y-6">
          <section>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Om oss</h1>
            <div className="mt-4 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Beredskapshubben Norden AB utvecklar och driver beredskapshubben.se – en digital beredskapsapp för mobil och web som hjälper hushåll att planera, bygga och följa upp sin hemberedskap, samt samarbeta med grannar och vänner.
              </p>
              <p>
                Vi tror att fler vill vara förberedda – men att många kanske saknar struktur, överblick och ett enkelt sätt att komma igång. Därför har vi skapat en tjänst som gör hemberedskap konkret, stegvis och genomförbar i vardagen, oavsett om du siktar på dagar, veckor eller månader.
              </p>
              <p className="font-medium text-foreground">Hemberedskap ska inte vara krångligt. Det ska vara tydligt.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Vad vi gör</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">Vi levererar en digital plattform där användare kan:</p>
            <ul className="mt-3 list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Skapa en strukturerad plan för sin hemberedskap</li>
              <li>Få tydliga checklistor och åtgärdsförslag</li>
              <li>Följa sin utveckling över tid</li>
              <li>Identifiera brister och prioritera rätt insatser</li>
              <li>Bygga beredskap steg för steg – i egen takt</li>
              <li>Samarbeta med grannar och vänner i beredskapsgrupper</li>
            </ul>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Appen är tillgänglig både via mobil och web, vilket gör det enkelt att arbeta med sin beredskap när och var det passar.
            </p>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Vår ambition är att kombinera teknik, pedagogik och användarvänlighet i en och samma lösning.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Vår vision</h2>
            <div className="mt-3 space-y-3 text-muted-foreground leading-relaxed">
              <p>Vi vill göra hemberedskap lika naturligt som att ha en hemförsäkring.</p>
              <p>
                Genom att erbjuda ett modernt digitalt verktyg sänker vi tröskeln från tanke till handling. Små, kontinuerliga förbättringar skapar långsiktig trygghet – både för individen och för samhället i stort.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Vilka vi är</h2>
            <div className="mt-3 space-y-3 text-muted-foreground leading-relaxed">
              <p>Beredskapshubben Norden AB är ett svenskt bolag med fokus på digital utveckling och samhällsnytta.</p>
              <p>
                Vi kombinerar tekniska lösningar med ett starkt engagemang för trygghet och självständighet. Vårt mål är att skapa en tjänst som hjälper svenska hushåll bygga och upprätthålla en god beredskap.
              </p>
              <p>Vi bygger långsiktigt, med kvalitet och trovärdighet i centrum.</p>
            </div>
          </section>

          <OfficialSourcesNotice />

          <section>
            <h2 className="text-xl font-semibold">Följ oss</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              På{' '}
              <a
                href="https://blogg.beredskapshubben.se/"
                className="text-primary hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                vår blogg
              </a>{' '}
              delar vi:
            </p>
            <ul className="mt-3 list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Praktiska beredskapstips och användarguider</li>
              <li>Fördjupningar inom hemberedskap</li>
              <li>Uppdateringar om appen</li>
              <li>Tankar om trygghet och samhällsberedskap</li>
            </ul>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Följ utvecklingen på{' '}
              <a
                href="https://beredskapshubben.se"
                className="text-primary hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                beredskapshubben.se
              </a>{' '}
              och bygg din beredskap tillsammans med oss.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Kontakt</h2>
            <div className="mt-3 space-y-3 text-muted-foreground">
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                Info: <a href="mailto:info@beredskapshubben.se" className="text-primary hover:underline">info@beredskapshubben.se</a>
              </p>
              <p className="flex items-center gap-2">
                <Headphones className="h-4 w-4 text-primary" />
                Support: <a href="mailto:support@beredskapshubben.se" className="text-primary hover:underline">support@beredskapshubben.se</a>
              </p>
              <p className="flex items-center gap-2">
                <Shield className="h-4 w-4 shrink-0 text-primary" />
                Integritetsfrågor: <a href="mailto:privacy@beredskapshubben.se" className="text-primary hover:underline">privacy@beredskapshubben.se</a>
              </p>
            </div>
          </section>
            </div>{/* /p-6 md:p-8 content */}
          </div>{/* /card */}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
